import {isNil} from 'ramda';

import {CompositionFont, useGetCompositionFontListQuery} from '@dms/api/sales';

import {getRegisteredFontFamily} from '../utils/getCompositionFontFamily';

const registeredFontIds = new Set<string>();

const registerFont = (font: CompositionFont) => {
  if (isNil(font.url) || registeredFontIds.has(font.id) || isNil(document.fonts)) {
    return;
  }
  registeredFontIds.add(font.id);
  const fontFace = new FontFace(getRegisteredFontFamily(font), `url(${font.url})`, {
    weight: font.weight.toString(),
    style: font.style,
  });
  document.fonts.add(fontFace);
  fontFace.load().catch(() => {
    document.fonts.delete(fontFace);
    registeredFontIds.delete(font.id);
  });
};

/**
 * Loads the tenant's uploaded composition fonts and registers each one with the browser so the
 * editor canvas renders text with the same font files the server uses for the final PNG.
 * Registration is idempotent per font id (module-level registry), so it runs during render, and
 * font faces stay registered for the page lifetime.
 * Fonts without a url (file missing) are skipped, so their text falls back to the built-in font.
 */
export function useCompositionFonts() {
  const {data: fonts, isLoading, isError} = useGetCompositionFontListQuery();

  fonts?.forEach(registerFont);

  return {fonts: fonts ?? [], isLoading, isError};
}
